import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import toast from 'react-hot-toast'
import { authStore } from '@/stores/AuthStore'

const LoginPage = observer(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const googleBtnRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const gisInitialized = useRef(false)

  const from = (location.state as any)?.from?.pathname || '/dashboard'

  // Redirect if already authenticated
  useEffect(() => {
    if (authStore.isAuthenticated && !authStore.isLoading) {
      navigate(from, { replace: true })
    }
  }, [authStore.isAuthenticated, from, navigate, authStore.isLoading])

  // Store navigate/from in refs so the GIS callback doesn't cause re-renders
  const navigateRef = useRef(navigate)
  const fromRef = useRef(from)
  navigateRef.current = navigate
  fromRef.current = from

  // Single effect: load script, fetch client ID, initialize GIS — runs once
  useEffect(() => {
    let cancelled = false

    async function init() {
      // 1. Fetch client ID
      let clientId: string | null = null
      try {
        const res = await fetch('/api/v1/auth/google/client-id')
        const data = await res.json()
        clientId = data.clientId || null
      } catch {
        clientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID || null
      }

      if (!clientId || cancelled) return

      // 2. Load GIS script
      await new Promise<void>((resolve) => {
        if ((window as any).google?.accounts?.id) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.onload = () => resolve()
        script.onerror = () => resolve()
        document.head.appendChild(script)
      })

      if (cancelled || !(window as any).google?.accounts?.id) return
      if (gisInitialized.current) return
      gisInitialized.current = true

      // 3. Initialize GIS — uses refs so this callback never changes
      ;(window as any).google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          setError(null)
          try {
            const res = await fetch('/api/v1/auth/google/signin', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ credential: response.credential }),
            })
            const data = await res.json()
            if (!res.ok) {
              setError(data.detail || data.error || 'Google sign-in failed')
              return
            }
            if (data.access_token) {
              localStorage.setItem('token', data.access_token)
              if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token)
              await authStore.checkAuth()
              navigateRef.current(fromRef.current, { replace: true })
            }
          } catch {
            // Fallback to redirect
            try { await authStore.loginWithGoogle() } catch {}
          }
        },
        auto_select: true,
        context: 'signin',
      })

      // 4. Render the button
      if (googleBtnRef.current) {
        ;(window as any).google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          shape: 'rectangular',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          width: 340,
          logo_alignment: 'left',
        })
      }

      // 5. Prompt One Tap
      ;(window as any).google.accounts.id.prompt()
    }

    init()
    return () => { cancelled = true }
  }, []) // Empty deps — runs exactly once

  const handleGoogleRedirect = async () => {
    try {
      await authStore.loginWithGoogle()
    } catch (error: any) {
      toast.error(authStore.error || 'Failed to initiate Google login')
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background: '#f0f0f3',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div
          style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '48px 40px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.1)',
          }}
        >
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <svg viewBox="0 0 56 56" fill="none" width="56" height="56">
                <rect width="56" height="56" rx="12" fill="#6C5CE7"/>
                <text x="28" y="39" textAnchor="middle" fill="white" fontSize="32" fontFamily="Inter, sans-serif" fontWeight="700">Z</text>
              </svg>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1a1a2e', letterSpacing: -0.5 }}>
              Agent Dashboard
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
              Sign in to continue
            </div>
          </div>

          {/* Error */}
          {(error || authStore.error) && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#dc2626',
                marginBottom: 16,
              }}
            >
              {error || authStore.error}
            </div>
          )}

          {/* Google Sign-In button — rendered by GIS */}
          <div style={{ display: 'flex', justifyContent: 'center', minHeight: 44, marginBottom: 4 }}>
            <div ref={googleBtnRef} />
          </div>

          {/* Fallback redirect button — hidden once GIS renders */}
          <button
            id="google-fallback-btn"
            onClick={handleGoogleRedirect}
            disabled={authStore.isLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              height: 44,
              background: '#ffffff',
              border: '1px solid #dadce0',
              borderRadius: 8,
              cursor: authStore.isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 500,
              color: '#3c4043',
              fontFamily: "'Inter', sans-serif",
              opacity: authStore.isLoading ? 0.6 : 1,
              marginTop: 8,
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {authStore.isLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 14, color: '#6b7280', marginTop: 16 }}>
            Sign in with your Google account to access the platform.
          </p>

          <div style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 24 }}>
            Agent Dashboard &mdash; AI agent orchestration platform
          </div>
        </div>
      </div>
    </div>
  )
})

export default LoginPage
